import React, { useId } from 'react';
import type { CountrySeed } from '../../lib/countries.seed';
import styles from './PhoneSegmented.module.css';

interface PhoneSegmentedProps {
    /** Visible label, already localized. */
    label: string;
    /**
     * The selected country object (drives the flag + dial code shown in
     * the read-only left segment). Source of truth lives in the parent's
     * country dropdown — this component DOES NOT mutate the country.
     */
    country: CountrySeed | undefined;
    /** National-significant phone digits (no leading dial code). */
    value: string;
    /** Called with the new national digits. */
    onChange: (value: string) => void;
    /** Localized placeholder, e.g. "5XXXXXXXX". */
    placeholder?: string;
    /** Disable the field (e.g. while submitting or on inactive country). */
    disabled?: boolean;
    /** Validation feedback styling. */
    feedback?: 'error' | 'warning' | 'success';
    /** Human-readable validation message under the input. */
    feedbackMessage?: string;
    /** UI locale — drives field direction. */
    locale: 'en' | 'ar';
    /** Optional id override (defaults to a `useId`-generated one). */
    id?: string;
    /** Optional name attribute. */
    name?: string;
    /** Optional autoComplete hint. */
    autoComplete?: string;
}

/**
 * Booksy-parity segmented phone input (AB#1516, CR-2026-05-04b AC-4):
 *
 * ```
 *   ┌─────────────┬───────────────────────────┐
 *   │ 🇦🇪 +971    │ 50 123 4567               │
 *   └─────────────┴───────────────────────────┘
 *      read-only      <input type="tel">
 * ```
 *
 * The left segment is a non-interactive flag + dial code that reflects the
 * parent form's selected country (the standalone `<CountrySelect>` is the
 * source of truth — this component is purely a renderer over it).
 *
 * Validation lives upstream in the form's Zod schema via
 * `isValidPhoneForCountry(value, country)` — libphonenumber-js handles
 * national-vs-E.164 ambiguity, so storing just the national digits in the
 * form state is fine.
 *
 * RTL: in AR the segmented control mirrors at the parent flexbox level.
 * The internal layout uses `direction: ltr` so the dial code visually
 * leads the number from the appropriate edge, matching the Booksy and
 * Fresha pattern.
 *
 * Why not `react-phone-number-input`'s integrated `<PhoneInput>`: that
 * component bundles its own country selector, which would conflict with
 * our standalone `<CountrySelect>` (per AC-1 country picker is the FIRST
 * field, separate from the phone). This component is a thin segmented
 * renderer; the heavy phone parsing comes from libphonenumber-js (already
 * a dependency).
 */
const PhoneSegmented: React.FC<PhoneSegmentedProps> = ({
    label,
    country,
    value,
    onChange,
    placeholder,
    disabled = false,
    feedback,
    feedbackMessage,
    locale,
    id,
    name,
    autoComplete = 'tel-national',
}) => {
    const generatedId = useId();
    const fieldId = id || `phone-${generatedId}`;
    const feedbackId = feedbackMessage ? `${fieldId}-feedback` : undefined;

    const dialCode = country?.dialCode ?? '+';
    // CR-2026-05-05a — SVG flag via `flag-icons` package. Replaces the
    // Unicode flag emoji that didn't render on Windows Segoe UI Emoji
    // (showed regional-indicator letter pairs instead — e.g. "AE").
    const flagClass = country ? `fi fi-${country.iso2Lower}` : '';

    return (
        <div className={styles.container}>
            <label htmlFor={fieldId} className={styles.label}>
                {label}
            </label>
            <div
                className={`${styles.segmented} ${disabled ? styles.disabled : ''} ${
                    feedback ? styles[feedback] : ''
                }`}
            >
                <div
                    className={styles.prefix}
                    aria-hidden="true"
                    title={country ? `${country.nameEn} (${dialCode})` : ''}
                >
                    {country ? (
                        <span className={`${flagClass} ${styles.flag}`} />
                    ) : (
                        <span className={styles.flag} />
                    )}
                    <span className={styles.dial}>{dialCode}</span>
                </div>
                <input
                    id={fieldId}
                    name={name}
                    type="tel"
                    inputMode="numeric"
                    autoComplete={autoComplete}
                    className={styles.input}
                    value={value}
                    onChange={(e) =>
                        onChange(e.target.value.replace(/[^\d ]/g, ''))
                    }
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={feedback === 'error' || undefined}
                    aria-describedby={feedbackId}
                    /* Always LTR for the digit input even in AR — phone
                       numbers are universally LTR. */
                    dir="ltr"
                    /* In AR the parent input visually flips so the digits
                       still read left-to-right after the dial code. */
                    style={
                        locale === 'ar'
                            ? { textAlign: 'right' }
                            : undefined
                    }
                />
            </div>
            {feedbackMessage && (
                <div
                    id={feedbackId}
                    className={`${styles.feedback} ${
                        feedback ? styles[feedback] : ''
                    }`}
                    role="alert"
                >
                    {feedbackMessage}
                </div>
            )}
        </div>
    );
};

export default PhoneSegmented;
